import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CaretLeft, CircleNotch } from '@phosphor-icons/react';
import { useDrawsUpdated } from '../hooks/useDrawsUpdated';
import W57VerdictCard from '../components/verdict/W57VerdictCard';
import TopProbablesBlock from '../components/verdict/TopProbablesBlock';
import WindowNarrative from '../components/WindowNarrative';
import { NARRATIVES } from '../components/windowNarratives';

const API = process.env.REACT_APP_BACKEND_URL;

const windowRoutes = {
  3: '/window3',
  5: '/window3-2',
  7: '/window7',
  14: '/window14',
  17: '/window17',
  22: '/window22',
  24: '/window24',
  41: '/summary',
  46: '/rebound-alert',
  51: '/digit-affinity',
  53: '/master-prediction',
  54: '/pattern-predictor',
  56: '/number-tier-consensus',
  60: '/draw-dna',
  61: '/dna-validation',
  63: '/rebound-alert?tab=reversion',
  64: '/window64',
};

// Display-label override (UI-only renumbering)
const windowLabel = {
  14: 'W2', // Renamed per user request — Sum Range is treated as "Pattern Window #2"
};
const dispW = (w) => windowLabel[w] || `W${w}`;

// Pattern Window IDs — grouped under a single parent block in W62
const PATTERN_WINDOW_IDS = new Set([3, 5, 7, 14, 17, 22, 24]);

// Per-window palette — each window group rendered in its own color
const windowColors = {
  3:  { bg: 'bg-sky-50',       border: 'border-sky-300',       head: 'bg-sky-100',       text: 'text-sky-900',       accent: 'text-sky-700'       },
  5:  { bg: 'bg-pink-50',      border: 'border-pink-300',      head: 'bg-pink-100',      text: 'text-pink-900',      accent: 'text-pink-700'      },
  7:  { bg: 'bg-orange-50',    border: 'border-orange-300',    head: 'bg-orange-100',    text: 'text-orange-900',    accent: 'text-orange-700'    },
  14: { bg: 'bg-yellow-50',    border: 'border-yellow-300',    head: 'bg-yellow-100',    text: 'text-yellow-900',    accent: 'text-yellow-700'    },
  17: { bg: 'bg-lime-50',      border: 'border-lime-300',      head: 'bg-lime-100',      text: 'text-lime-900',      accent: 'text-lime-700'      },
  22: { bg: 'bg-blue-50',      border: 'border-blue-300',      head: 'bg-blue-100',      text: 'text-blue-900',      accent: 'text-blue-700'      },
  24: { bg: 'bg-green-50',     border: 'border-green-300',     head: 'bg-green-100',     text: 'text-green-900',     accent: 'text-green-700'     },
  63: { bg: 'bg-slate-50',     border: 'border-slate-300',     head: 'bg-slate-200',     text: 'text-slate-900',     accent: 'text-slate-700'     },
  41: { bg: 'bg-amber-50',     border: 'border-amber-300',     head: 'bg-amber-100',     text: 'text-amber-900',     accent: 'text-amber-700'     },
  46: { bg: 'bg-rose-50',      border: 'border-rose-300',      head: 'bg-rose-100',      text: 'text-rose-900',      accent: 'text-rose-700'      },
  51: { bg: 'bg-purple-50',    border: 'border-purple-300',    head: 'bg-purple-100',    text: 'text-purple-900',    accent: 'text-purple-700'    },
  53: { bg: 'bg-emerald-50',   border: 'border-emerald-300',   head: 'bg-emerald-100',   text: 'text-emerald-900',   accent: 'text-emerald-700'   },
  54: { bg: 'bg-cyan-50',      border: 'border-cyan-300',      head: 'bg-cyan-100',      text: 'text-cyan-900',      accent: 'text-cyan-700'      },
  56: { bg: 'bg-teal-50',      border: 'border-teal-300',      head: 'bg-teal-100',      text: 'text-teal-900',      accent: 'text-teal-700'      },
  60: { bg: 'bg-indigo-50',    border: 'border-indigo-300',    head: 'bg-indigo-100',    text: 'text-indigo-900',    accent: 'text-indigo-700'    },
  61: { bg: 'bg-violet-50',    border: 'border-violet-300',    head: 'bg-violet-100',    text: 'text-violet-900',    accent: 'text-violet-700'    },
};
const defaultColor = { bg: 'bg-slate-50', border: 'border-slate-300', head: 'bg-slate-100', text: 'text-slate-900', accent: 'text-slate-700' };

const isHighConfidence = (pct) => typeof pct === 'number' && pct >= 90;
const isPerfect = (pct) => typeof pct === 'number' && pct >= 100;

const pctColor = (pct) => {
  if (typeof pct !== 'number') return 'text-gray-400';
  if (pct >= 90) return 'text-emerald-600';
  if (pct >= 70) return 'text-emerald-500';
  if (pct >= 50) return 'text-cyan-600';
  if (pct >= 30) return 'text-amber-600';
  return 'text-rose-500';
};

export default function ConsolidatedVerdict() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchData = (silent = false) => {
    if (!silent) setLoading(true);
    // Auth via HTTP-only cookies (axios.defaults.withCredentials set globally)
    axios.get(`${API}/api/consolidated-verdict`, { withCredentials: true })
      .then(r => { setData(r.data); setLastUpdated(new Date()); })
      .catch(() => {})
      .finally(() => { if (!silent) setLoading(false); });
  };

  useEffect(() => { fetchData(); }, []);
  useDrawsUpdated(() => fetchData(true));

  // 30s background polling (silent)
  useEffect(() => {
    const id = setInterval(() => fetchData(true), 30000);
    return () => clearInterval(id);
  }, []);

  // Refetch on tab focus (catches draws added while tab was hidden)
  useEffect(() => {
    const onVis = () => { if (document.visibilityState === 'visible') fetchData(true); };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  // Cross-tab sync: listen for localStorage signal or BroadcastChannel message
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === 'euromillions:last_draw_update') fetchData(true);
    };
    window.addEventListener('storage', onStorage);

    let bc;
    try {
      if (typeof BroadcastChannel !== 'undefined') {
        bc = new BroadcastChannel('euromillions');
        bc.onmessage = (ev) => { if (ev.data === 'draws-updated') fetchData(true); };
      }
    } catch (_e) { /* BroadcastChannel not supported — falls back to storage event */ }

    return () => {
      window.removeEventListener('storage', onStorage);
      if (bc) bc.close();
    };
  }, []);

  if (loading && !data) {
    return (
      <div className="min-h-screen bg-white text-gray-900 flex items-center justify-center">
        <CircleNotch size={40} className="text-emerald-500 animate-spin" />
      </div>
    );
  }

  if (!data || data.error) {
    return (
      <div className="min-h-screen bg-white text-gray-900 p-8">
        <button onClick={() => navigate('/')} className="text-gray-600 hover:text-gray-900 flex items-center gap-1 mb-4" data-testid="back-btn">
          <CaretLeft size={16} /> Back
        </button>
        <p className="text-rose-600">{data?.error || 'Failed to load'}</p>
      </div>
    );
  }

  const rows = data.rows || [];
  const windows = Array.from(new Set(rows.map(r => r.window))).sort((a, b) => a - b);
  const highConfCount = rows.filter(r => isHighConfidence(r.pct)).length;
  const perfectCount = rows.filter(r => isPerfect(r.pct)).length;

  const exportServer = (fmt) => {
    // Hit the backend export endpoint so the download matches what the
    // testing team / analyst sees on the server (Excel gets 3 sheets:
    // Verdicts + Top Probables + Meta; CSV gets a UTF-8 BOM header for
    // native Excel compatibility).
    const url = `${API}/api/consolidated-verdict/export?fmt=${fmt}`;
    const a = document.createElement('a');
    a.href = url;
    a.download = `consolidated-verdict-${data.pivot_draw_number || 'latest'}.${fmt === 'csv' ? 'csv' : 'xlsx'}`;
    a.click();
  };
  const exportCSV = () => exportServer('csv');
  const exportXLSX = () => exportServer('xlsx');

  return (
    <div className="min-h-screen bg-white text-gray-900" data-testid="consolidated-verdict-page">
      <header className="border-b border-gray-200 bg-white sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/')} className="text-gray-500 hover:text-gray-900 flex items-center gap-1 text-sm" data-testid="back-btn">
              <CaretLeft size={16} /> Dashboard
            </button>
            <div>
              <h1 className="text-lg font-bold text-gray-900">W62: Consolidated Verdict</h1>
              <p className="text-[11px] text-gray-500">Unified predictions from {data.window_count} windows. Each window color-coded. 90-100% rows are enlarged & bold.</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <div className="text-[10px] text-emerald-600 uppercase tracking-wider">Pivot</div>
              <div className="font-mono font-bold text-sm">#{data.pivot_draw_number}</div>
              <div className="text-[10px] text-gray-400">{data.pivot_draw_date}</div>
            </div>
            {lastUpdated && (
              <div className="text-right border-l border-gray-200 pl-3" data-testid="last-updated-block">
                <div className="text-[10px] text-gray-400 uppercase tracking-wider">Auto-sync</div>
                <div className="text-[10px] font-mono text-gray-500" data-testid="last-updated-time">{lastUpdated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</div>
                <button onClick={() => fetchData(true)} className="text-[10px] text-emerald-600 hover:underline" data-testid="refresh-now-btn">↻ refresh now</button>
              </div>
            )}
            <button onClick={exportCSV} className="px-3 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-bold rounded-lg transition-colors shadow-sm" data-testid="export-csv-btn">
              Export CSV
            </button>
            <button onClick={exportXLSX} className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded-lg transition-colors shadow-sm flex items-center gap-1" data-testid="export-xlsx-btn" title="Full 3-sheet workbook: Verdicts + Top Probables + Meta">
              Export Excel
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-5">


        <WindowNarrative windowId="W62" {...NARRATIVES.W62} className="mb-5" />
        {/* W57 Final Verdict — pinned at top */}
        <W57VerdictCard />

        {/* Top summary chips */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-2">
            <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">Total Windows</div>
            <div className="text-2xl font-mono font-black text-emerald-900">{data.window_count}</div>
          </div>
          <div className="bg-sky-50 border border-sky-200 rounded-lg px-4 py-2">
            <div className="text-[10px] font-bold uppercase tracking-wider text-sky-700">Total Projections</div>
            <div className="text-2xl font-mono font-black text-sky-900">{rows.length}</div>
          </div>
          <div className="bg-emerald-50 border-2 border-emerald-400 rounded-lg px-4 py-2">
            <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">90-100% Confidence</div>
            <div className="text-2xl font-mono font-black text-emerald-700">{highConfCount}</div>
          </div>
          {perfectCount > 0 && (
            <div className="bg-gradient-to-r from-yellow-300 to-amber-400 border-2 border-amber-500 rounded-lg px-4 py-2 shadow-md">
              <div className="text-[10px] font-bold uppercase tracking-wider text-amber-900">🎯 Perfect 100%</div>
              <div className="text-2xl font-mono font-black text-amber-950">{perfectCount}</div>
            </div>
          )}
        </div>

        {/* ============================================ */}
        {/* Block 0 — Top 10 Projected Probables per Ball */}
        {/* ============================================ */}
        <TopProbablesBlock topProbables={data.top_probables} />

        {/* Per-window grouped boxes */}
        {(() => {
          const patternWindows = windows.filter(w => PATTERN_WINDOW_IDS.has(w));
          const otherWindows = windows.filter(w => !PATTERN_WINDOW_IDS.has(w));

          const renderWindowBlock = (w) => {
            const wrows = rows.filter(r => r.window === w);
            const color = windowColors[w] || defaultColor;
            const firstRow = wrows[0];
            const hasPerfect = wrows.some(r => isPerfect(r.pct));
            return (
              <section
                key={w}
                className={`rounded-xl border-2 ${color.border} ${color.bg} overflow-hidden shadow-sm ${hasPerfect ? 'ring-2 ring-amber-300 ring-offset-2' : ''}`}
                data-testid={`window-block-${w}`}
              >
                <div className={`${color.head} px-5 py-3 border-b ${color.border} flex items-center justify-between`}>
                  <div className="flex items-center gap-3">
                    {windowRoutes[w] ? (
                      <button
                        onClick={() => navigate(windowRoutes[w])}
                        className={`font-mono font-black text-xl ${color.text} hover:underline decoration-current underline-offset-4`}
                        title={`Jump to ${firstRow.name}`}
                        data-testid={`window-link-${w}`}
                      >
                        {dispW(w)}
                      </button>
                    ) : (
                      <span className={`font-mono font-black text-xl ${color.text}`}>{dispW(w)}</span>
                    )}
                    <div>
                      {(() => {
                        const distinctBlocks = [...new Set(wrows.map(r => r.name))];
                        return (
                          <>
                            <div className={`text-sm font-bold ${color.text}`}>
                              {distinctBlocks.length === 1 ? distinctBlocks[0] : `${distinctBlocks.length} source blocks`}
                            </div>
                            <div className="text-[11px] text-gray-500">
                              {wrows.length} projection{wrows.length !== 1 ? 's' : ''}
                              {distinctBlocks.length > 1 && (
                                <span className="ml-1 text-gray-400">· {distinctBlocks.join(' · ')}</span>
                              )}
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  </div>
                  {hasPerfect && (
                    <span className="inline-block text-[11px] font-black uppercase tracking-wider bg-amber-400 text-amber-950 rounded-full px-3 py-1 animate-pulse">
                      🎯 Perfect signal
                    </span>
                  )}
                </div>

                <div className="overflow-x-auto bg-white">
                  <table className="w-full text-sm" data-testid={`window-table-${w}`}>
                    <thead className={`${color.head}`}>
                      <tr className={`text-[10px] uppercase tracking-wider ${color.accent} border-b ${color.border}`}>
                        <th className="px-3 py-2 text-left w-32">Projection Type</th>
                        <th className="px-3 py-2 text-center w-32">Projected</th>
                        <th className="px-3 py-2 text-center w-24">% Chance</th>
                        <th className="px-3 py-2 text-left w-64">Expected Numbers</th>
                        <th className="px-3 py-2 text-left">Summary</th>
                      </tr>
                    </thead>
                    <tbody>
                      {wrows.map((r, idx) => {
                        const highConf = isHighConfidence(r.pct);
                        const perfect = isPerfect(r.pct);
                        const rowCls = perfect
                          ? 'bg-yellow-50 border-t-4 border-b-4 border-amber-500 border-l-4 border-l-amber-500'
                          : highConf
                          ? 'bg-emerald-50/80 border-l-4 border-emerald-500'
                          : 'hover:bg-gray-50 border-l-2 border-transparent';
                        const textSize = highConf ? 'text-base' : 'text-sm';
                        const fontW = highConf ? 'font-bold' : 'font-medium';
                        const prevName = idx > 0 ? wrows[idx - 1].name : null;
                        const showBlockHeader = r.name !== prevName;
                        const distinctBlocksInW = new Set(wrows.map(x => x.name));
                        return (
                          <React.Fragment key={`${w}-${idx}`}>
                            {showBlockHeader && distinctBlocksInW.size > 1 && (
                              <tr className="bg-slate-100" data-testid={`block-header-${w}-${idx}`}>
                                <td colSpan={5} className="px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-700 border-t border-slate-300">
                                  <span className="inline-block px-2 py-0.5 bg-slate-700 text-white rounded text-[10px] mr-2">{dispW(w)} ▸ Block</span>
                                  {r.name}
                                </td>
                              </tr>
                            )}
                          <tr
                            className={`border-b border-gray-100 transition-colors ${rowCls}`}
                            data-testid={`verdict-${w}-${idx}`}
                          >
                            <td className={`px-3 py-2 font-mono ${textSize} ${fontW} text-gray-800`}>
                              {windowRoutes[w] ? (
                                <button
                                  onClick={() => navigate(`${windowRoutes[w]}${r.hash ? '#' + r.hash : ''}`)}
                                  className="text-left hover:underline decoration-dotted underline-offset-4 text-[#002FA7] font-mono"
                                  title={r.hash ? `Jump to ${dispW(w)} → ${r.hash}` : `Jump to ${dispW(w)}`}
                                  data-testid={`verdict-link-${w}-${idx}`}
                                >
                                  {r.projection_type}
                                </button>
                              ) : r.projection_type}
                            </td>
                            <td className="px-3 py-2 text-center">
                              {r.projection_type === 'Digit' ? (
                                <span className={`inline-flex rounded-full bg-emerald-100 text-emerald-800 font-mono font-black items-center justify-center ring-1 ring-emerald-300 ${highConf ? 'w-12 h-12 text-xl' : 'w-9 h-9 text-base'}`}>
                                  {r.projected}
                                </span>
                              ) : (
                                <span className={`font-mono ${fontW} text-gray-900 ${highConf ? 'text-lg' : textSize}`}>
                                  {r.projected}
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-2 text-center">
                              {typeof r.pct === 'number' && r.pct > 70 ? (
                                <span
                                  className={`inline-flex items-center justify-center rounded-full font-black text-white bg-emerald-600 ring-2 ring-emerald-300 shadow-md ${
                                    perfect ? 'w-16 h-16 text-base' : highConf ? 'w-14 h-14 text-sm' : 'w-12 h-12 text-xs'
                                  }`}
                                  data-testid={`pct-badge-${r.window}-${Math.round(r.pct)}`}
                                >
                                  {Math.round(r.pct)}%
                                </span>
                              ) : (
                                <span className={`font-mono font-black ${pctColor(r.pct)} ${perfect ? 'text-2xl' : highConf ? 'text-xl' : 'text-base'}`}>
                                  {typeof r.pct === 'number' ? `${r.pct}%` : r.pct}
                                </span>
                              )}
                            </td>
                            <td className={`px-3 py-2 font-mono text-gray-600 ${highConf ? 'text-sm font-bold text-gray-800' : 'text-[11px]'}`}>
                              {r.expected}
                            </td>
                            <td className={`px-3 py-2 text-gray-700 italic ${highConf ? 'text-sm font-semibold text-gray-900 not-italic' : 'text-[12px]'}`}>
                              {r.summary}
                            </td>
                          </tr>
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </section>
            );
          };

          return (
            <>
              {/* ============ PATTERN WINDOW PREDICTIONS — parent wrapper ============ */}
              {patternWindows.length > 0 && (
                <section className="rounded-xl border-4 border-fuchsia-400 bg-gradient-to-br from-fuchsia-50 via-pink-50 to-purple-50 shadow-xl overflow-hidden" data-testid="pattern-windows-wrapper">
                  <header className="bg-gradient-to-r from-fuchsia-600 via-pink-600 to-purple-600 px-5 py-3 text-white">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="inline-block px-2.5 py-1 bg-white/20 text-white rounded text-[11px] font-black tracking-wider backdrop-blur">PATTERN GROUP</span>
                      <h2 className="text-base font-black tracking-tight">Pattern Window Predictions</h2>
                      <span className="ml-auto text-[11px] font-mono bg-white/20 rounded-full px-2.5 py-0.5 backdrop-blur">
                        {patternWindows.length} windows · {patternWindows.map(dispW).join(' · ')}
                      </span>
                    </div>
                    <p className="text-[11px] text-white/90 mt-1">
                      Consolidated predictions from all pattern-detection windows (structural, range, appearance, similarity, shared-number).
                    </p>
                  </header>
                  <div className="p-4 space-y-4" data-testid="pattern-windows-inner">
                    {patternWindows.map(renderWindowBlock)}
                  </div>
                </section>
              )}

              {/* ============ OTHER WINDOWS (non-pattern) ============ */}
              {otherWindows.map(renderWindowBlock)}
            </>
          );
        })()}

        <div className="bg-gray-50 border border-gray-200 rounded-lg px-5 py-3 text-[11px] text-gray-600 flex items-center gap-4 flex-wrap">
          <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-amber-400 ring-1 ring-amber-500" /> 🎯 Perfect 100% — enlarged, bold</span>
          <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-emerald-400" /> 90-99% high confidence</span>
          <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-cyan-400" /> 50-70% moderate</span>
          <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-amber-300" /> 30-50% weak</span>
          <span className="ml-auto italic">Each window rendered in its own color block · Auto-updates on new draws</span>
        </div>
      </main>
    </div>
  );
}
